import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shared count-stabilization logic for the list-page filter pills (Units, Tenants, Service
 * Requests, Contractors, Payments, Payee Accounts, Manage Users, Feedback, ...).
 *
 * Why this exists: the backend computes aggregate counts AFTER applying the active
 * search/pill filter (`BaseService.ApplyPsf` runs `FullyQualifiedFilters` before
 * `HydrateMetaData`), so the `aggregateValues` that come back with a filtered response
 * describe the filtered set, not the full list. Without a cached baseline, selecting one
 * pill collapses every other pill's count to 0.
 *
 * The pill/dropdown counts are now sourced from dedicated `GET .../counts` endpoints
 * (see `useAggregateFilterCounts` below) rather than piggybacking on the paged row-list
 * response, so counts stay independent of the currently selected pill/dropdown and only
 * refetch on mount/scope/debounced-search.
 */

/** The synthetic pill key the backend registers for the unfiltered total. */
export const ALL_FILTER_KEY = "All";

/** A pill-key -> count map, as returned in the list response's `aggregateValues`. */
export type AggregateCounts = Record<string, number>;

export interface AggregateFilterOption {
  title: string;
  key?: string;
  count?: number;
}

export type AggregateFilterOptionWithCount = AggregateFilterOption & {
  count: number;
};

/**
 * After a mutation (add / edit / delete), refresh the baseline aggregate counts as well as
 * the rows when the page supplied a full-refresh handler; otherwise just refetch page 1.
 *
 * Returns which path was taken so the branch is assertable in a unit test.
 */
export function runFullRefresh(
  handleFullRefresh: (() => void) | undefined,
  fetchData: (page: number) => void
): "full" | "page" {
  if (handleFullRefresh) {
    handleFullRefresh();
    return "full";
  }

  fetchData(1);
  return "page";
}

export function getAggregateCount(
  aggregateValues: AggregateCounts | undefined | null,
  key: string,
): number {
  return Number(aggregateValues?.[key] ?? 0);
}

export function resolveAllFilterCount(
  aggregateValues: AggregateCounts | undefined | null,
  explicitTotal?: number | null,
): number {
  if (explicitTotal !== undefined && explicitTotal !== null) {
    return Number(explicitTotal);
  }

  if (!aggregateValues) return 0;

  if (Object.prototype.hasOwnProperty.call(aggregateValues, ALL_FILTER_KEY)) {
    return getAggregateCount(aggregateValues, ALL_FILTER_KEY);
  }

  return Object.entries(aggregateValues).reduce((sum, [key, value]) => {
    if (key === ALL_FILTER_KEY) return sum;
    return sum + Number(value);
  }, 0);
}

export function getVisibleAggregateOptions(
  aggregateValues: AggregateCounts | undefined | null,
  options: AggregateFilterOption[],
): AggregateFilterOptionWithCount[] {
  return options
    .map((option) => ({
      ...option,
      count: option.count ?? getAggregateCount(aggregateValues, option.key ?? option.title),
    }))
    .filter(
      // Always keep the "All" pill visible even at zero count — otherwise a search that
      // matches nothing hides every pill, leaving no in-page control to clear the pill
      // filter (the user would have to clear the search box instead).
      (option): option is AggregateFilterOptionWithCount =>
        (option.key ?? option.title) === ALL_FILTER_KEY || Number(option.count ?? 0) > 0,
    );
}

/**
 * When zero-count status pills are hidden, the previously selected filter can become an
 * orphan (e.g. default/URL "New" while every request is In Progress+). Keep the active
 * filter if it is still among the visible pills; otherwise fall back to All.
 */
export function resolveOrphanedAggregateFilter(
  activeFilter: string,
  visibleOptions: AggregateFilterOption[],
  fallback: string = ALL_FILTER_KEY,
): string {
  if (visibleOptions.length === 0) return activeFilter;
  const isVisible = visibleOptions.some(
    (option) => (option.key ?? option.title) === activeFilter,
  );
  return isVisible ? activeFilter : fallback;
}

/**
 * Orphan resolve must wait until counts exist. While loading, getVisibleAggregateOptions
 * treats unknown pills as 0 and may show only All — resolving then incorrectly resets
 * dashboard deep-links (?selectedOption=New) to All (bug/dash-sr-pay-regress).
 */
export function shouldResolveOrphanedAggregateFilter(
  countsLoading: boolean,
  aggregateValues: AggregateCounts | undefined | null,
): boolean {
  return !countsLoading && aggregateValues != null;
}

interface UseAggregateCountsParams {
  fetchCounts: (searchOnlyFilter: string | null) => Promise<AggregateCounts | undefined>;
  searchOnlyFilter: string | null;
  scopeKey: string;
}

/**
 * Dedicated counts-endpoint loader shared by every list page in the pill-count refactor.
 * Re-fetches only when the debounced search-only filter or the navigation scope changes.
 */
export function useAggregateFilterCounts({
  fetchCounts,
  searchOnlyFilter,
  scopeKey,
}: UseAggregateCountsParams) {
  const [aggregateValues, setAggregateValues] = useState<AggregateCounts | undefined>(undefined);
  const [baselineAggregateValues, setBaselineAggregateValues] = useState<AggregateCounts | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);
  const baselineRef = useRef<AggregateCounts | undefined>(undefined);
  const scopeKeyRef = useRef(scopeKey);

  const loadCounts = useCallback(
    async (refreshBaseline = false) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
        const shouldFetchBaseline = refreshBaseline || !baselineRef.current || !searchOnlyFilter;
        const [liveCounts, baselineCounts] = shouldFetchBaseline && searchOnlyFilter
          ? await Promise.all([fetchCounts(searchOnlyFilter), fetchCounts(null)])
          : [await fetchCounts(searchOnlyFilter), undefined];

        if (requestId !== requestIdRef.current) return undefined;

        setAggregateValues(liveCounts);
        if (!searchOnlyFilter) {
          baselineRef.current = liveCounts;
          setBaselineAggregateValues(liveCounts);
        } else if (baselineCounts) {
          baselineRef.current = baselineCounts;
          setBaselineAggregateValues(baselineCounts);
        }

        return liveCounts;
      } catch (error) {
        if (requestId === requestIdRef.current) {
          console.error(error);
        }
        return undefined;
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [fetchCounts, searchOnlyFilter],
  );

  useEffect(() => {
    // A scope change (e.g. navigating from one Property/Unit detail tab to another) means
    // the previously cached baseline no longer describes the current scope. Reset it before
    // `loadCounts` runs so a scope switch while a search term is active re-fetches a fresh
    // baseline instead of silently reusing the prior scope's totals (see the "scope changes"
    // fix in the list-filter-counts PR review).
    if (scopeKeyRef.current !== scopeKey) {
      scopeKeyRef.current = scopeKey;
      baselineRef.current = undefined;
      setBaselineAggregateValues(undefined);
    }
    loadCounts();
  }, [loadCounts, scopeKey]);

  const refreshCounts = useCallback(async () => {
    return loadCounts(true);
  }, [loadCounts]);

  const getVisibleOptions = useCallback(
    (options: AggregateFilterOption[]) => getVisibleAggregateOptions(aggregateValues, options),
    [aggregateValues],
  );

  const getCount = useCallback(
    (key: string, options?: { baseline?: boolean }) =>
      getAggregateCount(options?.baseline ? baselineAggregateValues : aggregateValues, key),
    [aggregateValues, baselineAggregateValues],
  );

  return {
    aggregateValues,
    baselineAggregateValues,
    loading,
    refreshCounts,
    getVisibleOptions,
    getCount,
  };
}
